import React, { useState, useEffect } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import SettlementStatus from '../components/SettlementStatus';
import { formatCurrency, betCategory, shortenPlayerName, livePickProgress } from '../utils/settlementUtils';

// World Cup props are anytime-goalscorer yes/no modeled as over/under 0.5
const isGoalscorerStat = (stat) => /goalscorer/i.test(stat || '');
const pickLabel = (pick) => {
  if (isGoalscorerStat(pick.statType)) {
    return (pick.pickType || '').toLowerCase() === 'under' ? 'No Goal' : 'To Score';
  }
  return `${pick.statType} ${String(pick.pickType || '').toUpperCase()} ${pick.line}`;
};
// Fixture label e.g. "Bayern Munich vs Elversberg"; null for legacy picks.
const matchLabel = (pick) => (pick?.homeTeam && pick?.awayTeam) ? `${pick.homeTeam} vs ${pick.awayTeam}` : null;

// Live in-play progress bar under a pick. Renders nothing unless the game is live
// with a real value. `rich` (modal) adds a LIVE badge, readout, and "as of" time.
const TONE_BAR = { good: 'bg-green-500', warn: 'bg-amber-400', bad: 'bg-red-500' };
const TONE_TEXT = { good: 'text-green-400', warn: 'text-amber-400', bad: 'text-red-400' };
const LivePickBar = ({ pick, rich = false }) => {
  const p = livePickProgress(pick);
  if (!p) return null;
  const pct = Math.round(p.ratio * 100);
  const readout = `${p.currentLabel}${p.targetLabel ? ` · ${p.targetLabel}` : ''}`;
  return (
    <div className={`w-full ${rich ? 'mt-2' : 'mt-1'}`}>
      {rich && (
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />LIVE
          </span>
          <span className={`text-xs font-semibold ${TONE_TEXT[p.tone] || 'text-gray-300'}`}>{readout}</span>
          {p.asOf && <span className="text-[10px] text-gray-500 ml-auto">as of {p.asOf}</span>}
        </div>
      )}
      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ease-out ${TONE_BAR[p.tone] || 'bg-green-500'}`}
             style={{ width: `${pct}%` }} />
      </div>
      {!rich && (
        <div className="text-[10px] text-gray-400 mt-0.5">
          <span className="text-red-400">● </span>{readout}{p.asOf ? `  ·  ${p.asOf}` : ''}
        </div>
      )}
    </div>
  );
};

const UserBetsPage = ({ user, onBack }) => {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // open, settled
  const [selectedBet, setSelectedBet] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  useEffect(() => {
    fetchUserBets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Poll (~40s) while the Open tab has a live in-play pick, so the progress bars
  // advance without a manual refresh. Only hits our bets API (DB read).
  useEffect(() => {
    if (filter !== 'open') return undefined;
    const hasLive = bets.some((b) =>
      (b.picks || []).some((p) => String(p.liveState || '').toLowerCase() === 'live'));
    if (!hasLive) return undefined;
    const id = setInterval(() => { fetchUserBets(); }, 40000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, bets]);

  const fetchUserBets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/bets/user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('playerbet_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBets(data.bets || []);
      }
    } catch (error) {
      console.error('Error fetching user bets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBets = bets.filter(bet => betCategory(bet) === filter);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold">My Bets</h1>
            <p className="text-blue-100 opacity-90">Track your betting history</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-gray-800">
        <div className="flex flex-wrap gap-2">
          {['open', 'settled'].map((filterType) => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === filterType 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Bets List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <div className="text-gray-400">Loading your bets...</div>
          </div>
        ) : filteredBets.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400">No bets found</div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBets.map((bet) => (
              <div
                key={bet.id}
                onClick={() => setSelectedBet(bet)}
                className="bg-gray-900 rounded-lg p-6 border border-gray-700 cursor-pointer hover:border-blue-500/50 transition-colors"
              >
                {/* Bet Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-lg font-semibold">Bet ID: {bet.reference || `#${bet.id}`}</div>
                    <div className="text-sm text-gray-400">
                      {new Date(bet.createdAt).toLocaleDateString()} • 
                      {bet.pickCount} pick{bet.pickCount !== 1 ? 's' : ''} • 
                      {bet.type === 'PerfectPick' ? 'Beast Mode' : 'Safety Play'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{formatCurrency(bet.amount)}</div>
                    <div className="text-sm text-gray-400">Wagered</div>
                  </div>
                </div>

                {/* Settlement Status */}
                <SettlementStatus bet={bet} />

                {/* Picks Summary */}
                {bet.picks && bet.picks.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="text-sm font-medium text-gray-400 mb-2">Picks:</div>
                    <div className="space-y-2">
                      {bet.picks.slice(0, 3).map((pick) => (
                        <div key={pick.id} className="flex justify-between items-start text-sm gap-3">
                          <div className="flex-1 min-w-0">
                            <span className="text-white">{shortenPlayerName(pick.playerName)}</span>
                            <span className="text-gray-400 ml-2">
                              {pick.statType} {pick.pickType} {pick.line}
                            </span>
                            {matchLabel(pick) && (
                              <div className="text-gray-500 text-xs mt-0.5">{matchLabel(pick)}</div>
                            )}
                            <LivePickBar pick={pick} />
                          </div>
                          <div className={`px-2 py-1 rounded text-xs ${
                            pick.status === 'Won' ? 'bg-green-500/20 text-green-400' :
                            pick.status === 'Lost' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {pick.status}
                          </div>
                        </div>
                      ))}
                      {bet.picks.length > 3 && (
                        <div className="text-xs text-blue-400 font-medium">
                          +{bet.picks.length - 3} more picks · click to view all
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bet detail modal */}
      {selectedBet && (
        <div
          onClick={() => setSelectedBet(null)}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-gray-900 rounded-2xl border border-gray-700"
          >
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 p-5 flex justify-between items-center rounded-t-2xl">
              <div>
                <div className="text-lg font-bold">Bet ID: {selectedBet.reference || `#${selectedBet.id}`}</div>
                <div className="text-sm text-blue-100 opacity-90">
                  {selectedBet.type === 'PerfectPick' ? 'Beast Mode' : 'Safety Play'} · {(selectedBet.picks || []).length} pick{(selectedBet.picks || []).length !== 1 ? 's' : ''}
                </div>
              </div>
              <button onClick={() => setSelectedBet(null)} className="p-2 text-white/80 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-5">
              {/* Summary */}
              <div className="bg-gray-800/60 rounded-lg p-4 mb-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Placed</span><span>{selectedBet.createdAt ? new Date(selectedBet.createdAt).toLocaleString() : '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Wagered</span><span className="font-semibold">{formatCurrency(selectedBet.amount)}</span></div>
                {selectedBet.potentialPayout != null && (
                  <div className="flex justify-between"><span className="text-gray-400">Potential payout</span><span className="font-semibold text-green-400">{formatCurrency(selectedBet.potentialPayout)}</span></div>
                )}
                {selectedBet.actualPayout != null && selectedBet.actualPayout > 0 && (
                  <div className="flex justify-between"><span className="text-gray-400">Payout</span><span className="font-semibold text-green-400">{formatCurrency(selectedBet.actualPayout)}</span></div>
                )}
              </div>
              <SettlementStatus bet={selectedBet} />

              {/* Full picks */}
              <div className="mt-5">
                <div className="text-sm font-medium text-gray-400 mb-3">Picks ({(selectedBet.picks || []).length})</div>
                <div className="space-y-2">
                  {(selectedBet.picks || []).map((pick, i) => (
                    <div key={pick.id ?? i} className="flex justify-between items-start bg-gray-800/40 rounded-lg p-3 gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-blue-400 shrink-0">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium">{shortenPlayerName(pick.playerName)}</div>
                          <div className="text-gray-400 text-xs">
                            {pickLabel(pick)}{pick.odds ? `  ·  @ ${pick.odds}` : ''}
                          </div>
                          {matchLabel(pick) && (
                            <div className="text-gray-500 text-xs mt-0.5">{matchLabel(pick)}</div>
                          )}
                          <LivePickBar pick={pick} rich />
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs ${
                        pick.status === 'Won' ? 'bg-green-500/20 text-green-400' :
                        pick.status === 'Lost' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {pick.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBetsPage;